/* RESPONSIBLE TEAM: team-pricing-and-packaging */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type { Router } from '@ember/routing';
import type Store from '@ember-data/store';
import type GuideLibraryService from 'embercom/services/guide-library-service';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { post } from 'embercom/lib/ajax';
import type { Email } from 'embercom/components/multi-email-input';
import type { TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { setOwner } from '@ember/application';
import {
  PRICING_5_X_FULL_SEAT_TYPE,
  COPILOT_SEAT_TYPE,
} from 'embercom/lib/settings/seats/constants';

export class TeammatesInviter {
  @service declare appService: AppService;
  @service declare router: Router;
  @service declare store: Store;
  @service declare guideLibraryService: GuideLibraryService;

  @tracked allEmails: Email[] = [];
  @tracked partialInput = '';

  constructor(owner: unknown) {
    setOwner(this, owner);
  }

  get validEmails() {
    return this.allEmails.filter((email) => !email.error);
  }

  get sendInviteButtonDisabled() {
    let invalidEmails = this.allEmails.filter((email) => !!email.error);
    return (
      (!this.allEmails.length && !this.partialInput) ||
      invalidEmails.length !== 0 ||
      !this.app.invitingPermitted
    );
  }

  private get app() {
    return this.appService.app;
  }

  @action
  setPartialInput(string: string) {
    this.partialInput = string;
  }

  @action
  onEmailUpdate(emails: Email[]) {
    this.allEmails = emails;
  }

  @action
  navigateToInvitePage() {
    this.router.transitionTo('apps.app.settings.workspace.teammates.invite.new', {
      queryParams: {
        emails:
          this.validEmails.length > 0
            ? this.validEmails.map((validEmail) => validEmail.value).join(',')
            : undefined,
      },
    });
  }

  @action async performInvite(roleId?: string, copilotAccess?: boolean) {
    await taskFor(this.inviteTeammate).perform(roleId, copilotAccess);

    if (this.guideLibraryService.canUseGuideLibraryService) {
      await this.guideLibraryService.markStepCompleted(
        'guide_library_foundational_steps_invite_teammates',
      );
    }
  }

  @task({ drop: true }) private *inviteTeammate(
    roleId?: string,
    copilotAccess?: boolean,
  ): TaskGenerator<void> {
    let permissions = this.createPermissionsObject(roleId);
    let seatTypes = copilotAccess
      ? [PRICING_5_X_FULL_SEAT_TYPE, COPILOT_SEAT_TYPE]
      : [PRICING_5_X_FULL_SEAT_TYPE];

    let invitees = this.validEmails.map((email) => {
      return {
        email: email.value,
        app_id: this.app.id,
        seat_types: seatTypes,
        ...permissions,
      };
    });

    if (invitees.length === 0) {
      return;
    }

    let response = yield post('/ember/invite_admin_groups', {
      invitees,
      app_id: this.app.id,
    });

    response.forEach((invite: any) => {
      this.store.pushPayload({ 'invited-admin': invite });
    });
  }

  private createPermissionsObject(roleId?: string) {
    let permissionsObject = this.store.createRecord('permission', {
      app_id: this.app.id,
      ...(roleId ? { role_id: roleId } : {}),
    });

    permissionsObject.setAllPermissions(true);
    return permissionsObject.serialize();
  }
}
